import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button } from '../../components/ui'
import { listingStatuses, setListingStatus } from '../../api/objects-write'
import type { ListingStatus } from '../../api/objects-write'
import { t } from '../../i18n'

/**
 * The "Let" and "No longer available" buttons.
 *
 * Two of the ten statuses are one click, because they are the two that happen
 * every week; the rest are in the list behind them. Any of them may be set from
 * any other — the owner decided that the arrows describe how a listing moves on
 * its own, and somebody standing in the flat knows things the arrows do not:
 * that a let fell through, that a withdrawn flat is back on, that the wrong row
 * was clicked.
 *
 * The reason is free text, by the same decision. A list of codes was offered and
 * turned down, and a sentence somebody typed is worth more here than a category
 * they picked to get past the form. It is optional, and the hint says why
 * writing it anyway is worth the four seconds.
 */
export function StatusPicker({
  listingID,
  current,
  propertyID,
}: {
  readonly listingID: string
  readonly current: string
  readonly propertyID: string
}) {
  const [chosen, setChosen] = useState<ListingStatus | null>(null)
  const [reason, setReason] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const client = useQueryClient()

  const move = useMutation({
    mutationFn: (status: ListingStatus) => setListingStatus(listingID, status, reason.trim()),
    onSuccess: () => {
      setChosen(null)
      setReason('')
      setFailure(null)
      void client.invalidateQueries({ queryKey: ['property', propertyID] })
    },
    onError: (error: Error) => setFailure(error.message),
  })

  if (chosen === null) {
    return (
      <div className="status-picker">
        {(['rented', 'withdrawn'] as const)
          .filter((status) => status !== current)
          .map((status) => (
            <Button key={status} variant="secondary" onClick={() => setChosen(status)}>
              {t(`status.${status}`)}
            </Button>
          ))}

        <details className="status-picker__more">
          <summary>{t('status.set')}</summary>
          <ul className="status-picker__list">
            {listingStatuses
              .filter((status) => status !== current)
              .map((status) => (
                <li key={status}>
                  <button
                    type="button"
                    className="status-picker__option"
                    onClick={() => setChosen(status)}
                  >
                    {t(`status.${status}`)}
                  </button>
                </li>
              ))}
          </ul>
        </details>
      </div>
    )
  }

  return (
    <form
      className="build-form"
      onSubmit={(event) => {
        event.preventDefault()
        move.mutate(chosen)
      }}
    >
      <p className="build-form__hint">
        {t('status.set')}: <strong>{t(`status.${chosen}`)}</strong>
      </p>

      <div className="field">
        <label className="field__label" htmlFor={`status-reason-${listingID}`}>
          {t('status.reason')}
        </label>
        <input
          id={`status-reason-${listingID}`}
          className="field__input"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('status.reasonPlaceholder')}
          autoFocus
        />
        <p className="field__hint">{t('status.reasonHint')}</p>
      </div>

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="build-form__actions">
        <Button type="submit" busy={move.isPending}>
          {move.isPending ? t('status.applying') : t('status.apply')}
        </Button>
        <Button variant="secondary" onClick={() => setChosen(null)} disabled={move.isPending}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
